import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import { ControlPosition, MapControl } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import { makeSxStyles, useDebounce } from "ui";
import ArrowSelectorTool from "../arrow-selector";
import LassoSelectorTool from "../lasso-selector";
import { AddOutlined, RemoveOutlined, Search } from "@mui/icons-material";
import { MapMarker, LassoMode } from "./map.types";

export enum ZoomChange {
  Increase,
  Decrease,
}

type TopMapControlProps = {
  onDrawingChange: (drawing: boolean, mode?: LassoMode) => void;
  drawing: boolean;
  mapFilters?: React.ReactNode;
  selectedAddresses?: MapMarker[];
  onResetSelection?: () => void;
};

type ZoomMapControlsProps = {
  onZoomChange: (zoom: ZoomChange) => void;
};

type SearchLocationAddressProps = {
  markers: MapMarker[];
  searchOptions: MapMarker[];
  isLoading: boolean;
  query: string;
  setMapCentering: (id: string | undefined, addresses?: MapMarker[]) => void;
  setQuery: (query: string) => void;
  setSelectedAddress: (address: MapMarker[], multiSelect?: boolean) => void;
  onAddCustomer?: (searchedAddress: string) => void;
  onSearchResultSelect?: (marker: MapMarker) => void;
};

export const TopMapControl = ({
  onDrawingChange,
  drawing,
  mapFilters,
  selectedAddresses = [],
  onResetSelection,
}: TopMapControlProps) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const hasActiveSelection = selectedAddresses.length > 0;
  const shouldShowDropdown = hasActiveSelection && !drawing;

  const handleLassoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (shouldShowDropdown) {
      setAnchorEl(event.currentTarget);
    } else {
      onDrawingChange(true, LassoMode.REPLACE);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewSelection = () => {
    handleClose();
    onResetSelection?.();
    onDrawingChange(true, LassoMode.REPLACE);
  };

  const handleAddToSelection = () => {
    handleClose();
    onDrawingChange(true, LassoMode.ADD);
  };

  const handleRemoveSelection = () => {
    handleClose();
    // Clear the whole selection (same behaviour as the sidebar "Reset" button)
    // rather than entering a draw-to-subtract mode.
    onResetSelection?.();
  };

  return (
    <MapControl position={ControlPosition.TOP_LEFT}>
      <Box sx={styles.menuBox}>
        <Button
          sx={styles.menuBoxItem}
          onClick={() => {
            onDrawingChange(false);
          }}
          className={!drawing ? "menuBoxItemActive" : ""}
        >
          <ArrowSelectorTool />
        </Button>
        <Button
          sx={styles.menuBoxItem}
          className={drawing ? "menuBoxItemActive" : ""}
          onClick={handleLassoClick}
          aria-controls={open ? "lasso-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <LassoSelectorTool />
        </Button>
        <Menu
          id="lasso-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "lasso-button",
          }}
        >
          <MenuItem onClick={handleNewSelection}>New Selection</MenuItem>
          <MenuItem onClick={handleAddToSelection}>Add to Selection</MenuItem>
          <MenuItem onClick={handleRemoveSelection}>Remove Selection</MenuItem>
        </Menu>
        {mapFilters}
      </Box>
    </MapControl>
  );
};

export const AddressBarControl = ({
  markers,
  searchOptions = [],
  isLoading,
  query,
  setQuery,
  setSelectedAddress,
  setMapCentering,
  onAddCustomer,
  onSearchResultSelect,
}: SearchLocationAddressProps) => {
  const styles = useStyles();
  const [address, setAdddress] = useState<string>("");
  const { debounce } = useDebounce(475);

  useEffect(() => {
    setAdddress(query);
  }, [query]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdddress(event.target.value);
    debounce(() => setQuery(event.target.value));
  };

  const selectAddress = (res: MapMarker) => {
    if (onSearchResultSelect) {
      // Use custom handler if provided
      onSearchResultSelect(res);
    } else {
      // Default behavior
      const fullMarkerDetails = markers.find((marker) => marker.id === res.id);
      if (!fullMarkerDetails) return;
      setSelectedAddress([fullMarkerDetails], true);
      setMapCentering(undefined, [fullMarkerDetails]);
    }
    setQuery("");
    setAdddress("");
  };

  const selectAllAddresses = () => {
    // Call selectAddress for each search option individually, exactly like clicking them
    // This ensures all edge cases (unassigned regions, etc.) are handled correctly
    searchOptions.forEach((result) => {
      selectAddress(result);
    });
  };

  const handleAddCustomer = () => {
    onAddCustomer?.(query);
    setQuery("");
    setAdddress("");
  };

  return (
    <MapControl position={ControlPosition.TOP_RIGHT}>
      <Box sx={styles.searchWrapper}>
        <TextField
          placeholder="Search Address"
          value={address}
          onChange={handleChange}
          sx={styles.searchInput}
          fullWidth
          InputProps={{
            startAdornment: <Search sx={{ marginRight: "5px" }} />,
            sx: {
              padding: 0,
              margin: 0,
              border: "none",
            },
            endAdornment: isLoading && <CircularProgress size={20} />,
          }}
        />

        {!!query.length && !isLoading && (
          <List sx={styles.resultsMenu}>
            {searchOptions.length ? (
              <>
                {searchOptions.map((result, index) => (
                  <ListItem
                    key={index}
                    sx={styles.listItem}
                    onClick={() => selectAddress(result)}
                  >
                    {result.address}
                  </ListItem>
                ))}
                {searchOptions.length > 1 && (
                  <ListItem
                    key="select-all"
                    sx={styles.listItem}
                    onClick={selectAllAddresses}
                  >
                    All Addresses for "{query}"
                  </ListItem>
                )}
              </>
            ) : (
              <>
                <ListItem key="no-results-found" sx={styles.listItem}>
                  No results found for "{query}". Try adjusting the filters.
                </ListItem>
                {onAddCustomer && query.length > 3 && (
                  <ListItem key="add-customer" sx={styles.addCustomerItem}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleAddCustomer}
                    >
                      Add as Customer
                    </Button>
                  </ListItem>
                )}
              </>
            )}
          </List>
        )}
      </Box>
    </MapControl>
  );
};

export const ZoomMapControls = ({ onZoomChange }: ZoomMapControlsProps) => {
  const styles = useStyles();

  return (
    <MapControl position={ControlPosition.INLINE_END_BLOCK_END}>
      <Box sx={styles.zoomControls}>
        <Button
          sx={styles.zoomOption}
          onClick={() => onZoomChange(ZoomChange.Increase)}
        >
          <AddOutlined sx={{ color: "#C1C7CE" }} />
        </Button>
        <Button
          sx={styles.zoomOption}
          onClick={() => onZoomChange(ZoomChange.Decrease)}
        >
          <RemoveOutlined sx={{ color: "#C1C7CE" }} />
        </Button>
      </Box>
    </MapControl>
  );
};

const useStyles = makeSxStyles(() => ({
  menuBox: {
    display: "flex",
    flexDirection: "row",
    minWidth: "96px",
    height: "48px",
    position: "absolute",
    top: 20,
    left: 20,
    gap: "12px",
    backgroundColor: "#1A1C1E",
    borderRadius: "25px",
    padding: "6px",
    justifyContent: "space-between",
  },
  menuBoxItem: {
    width: "36px",
    height: "36px",
    minWidth: "unset",
    padding: "unset",
    borderRadius: "100%",
    color: "#C1C7CE",

    "&.menuBoxItemActive": {
      textDecoration: "none",
      backgroundColor: "#CAC4D01F !important",
    },
  },
  filterMenu: {
    marginTop: "8px",
    "& .MuiMenu-list": {
      backgroundColor: "#1A1C1E",
      minWidth: "320px",
    },
    "& .MuiMenu-list > li.MuiButtonBase-root ": {
      paddingTop: "8px",
      paddingBottom: "8px",
    },
    "& .MuiMenu-list > li label": {
      "& .MuiFormControlLabel-label": {
        fontSize: "16px",
        color: "#C5C6C9",
      },
      marginLeft: "0",
      width: "100%",
      justifyContent: "space-between",
    },
  },
  zoomControls: {
    display: "flex",
    backgroundColor: "#1A1C1E",
    flexDirection: "column",
    height: "96px",
    width: "48px",
    borderRadius: "25px",
    position: "absolute",
    bottom: 20,
    right: 20,
  },
  zoomOption: {
    width: "48px",
    height: "48px",
    minWidth: "unset",
    padding: "unset",
  },
  searchWrapper: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: 20,
    right: 20,
  },
  searchInput: {
    width: "300px",
    height: "48px",
    borderRadius: "99px",
    backgroundColor: "#1A1C1E",
    padding: "12px 16px",
    color: "#C5C6C9",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "0",
    },
    "& .MuiOutlinedInput-root": {
      height: "100%",
    },

    "& .MuiOutlinedInput-input:-webkit-autofill": {
      WebkitBoxShadow: "none",
      height: "60%",
      background: "none !important",
    },
  },
  resultsMenu: {
    marginTop: "10px",
    backgroundColor: "#1E2022",
    borderRadius: "4px",
    maxHeight: "250px",
    overflowY: "auto",
  },
  listItem: {
    "&:hover": {
      backgroundColor: "rgba(230, 224, 233, 0.08)",
      cursor: "pointer",
    },
  },
  addCustomerItem: {
    padding: "8px 12px",
  },
  switchLabelStyle: {
    color: "#C5C6C9",
    fontSize: "16px",
  },
}));
